/**
 * DealerConnect Demo Configuration
 *
 * Shared configuration and initialization logic for the demo.
 * Separated to avoid circular dependencies between demo-app.ts and main-page.ts.
 */

import type {
  DealerConnectEntryPoint,
  DealerConnectEvent,
  DealerConnectExternalAuth,
  DealerConnectPreConfigureResult
} from './package/dealerconnect-entrypoint-intf';

/**
 * API server configuration loaded from apiserver.json
 */
interface ApiServerConfig {
  host: string;
  dealerConnectClientId?: string;
  dealerConnectDemoUserToken?: string;
  dealerConnectVersion?: string;
}

// Demo configuration constants
export const DEMO_CONFIG = {
  // Default API endpoint - will be overridden by apiserver.json if available
  defaultApiEndpoint: 'https://localhost:44348'
};

// Cached API endpoint from apiserver.json
let _apiEndpoint: string | undefined;
let _dealerConnectClientId: string | undefined;
let _dealerConnectDemoUserToken: string | undefined;
let _dealerConnectVersion: string | undefined;

interface TwoFactorRequestEventDetail {
  requestId: string;
  prompt: string;
  createdAtIso: string;
}

interface TwoFactorProvideEventDetail {
  requestId: string;
  twoFactorCode: string;
}

interface TwoFactorCancelEventDetail {
  requestId: string;
}

function generateRequestId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return 'req_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2);
  }
}

async function requestTwoFactorCodeFromUi(): Promise<string> {
  const requestId = generateRequestId();

  const detail: TwoFactorRequestEventDetail = {
    requestId,
    prompt: 'Enter 2FA code',
    createdAtIso: new Date().toISOString()
  };

  console.log('[DealerConnect Demo] Requesting 2FA code (requestId):', requestId);
  window.dispatchEvent(new CustomEvent<TwoFactorRequestEventDetail>('dealerconnect-request-2fa', { detail }));

  return await new Promise<string>((resolve, reject) => {
    const onProvide = (e: Event) => {
      const custom = e as CustomEvent<TwoFactorProvideEventDetail>;
      if (!custom.detail || custom.detail.requestId !== requestId) return;
      cleanup();

      const code = (custom.detail.twoFactorCode ?? '').trim();
      if (!code) {
        reject(new Error('2FA code was empty'));
        return;
      }

      resolve(code);
    };

    const onCancel = (e: Event) => {
      const custom = e as CustomEvent<TwoFactorCancelEventDetail>;
      if (!custom.detail || custom.detail.requestId !== requestId) return;
      cleanup();
      reject(new Error('2FA entry cancelled'));
    };

    const cleanup = () => {
      window.removeEventListener('dealerconnect-provide-2fa', onProvide);
      window.removeEventListener('dealerconnect-cancel-2fa', onCancel);
    };

    window.addEventListener('dealerconnect-provide-2fa', onProvide);
    window.addEventListener('dealerconnect-cancel-2fa', onCancel);
  });
}

/**
 * Load API endpoint from apiserver.json or use default
 */
async function loadApiEndpoint(): Promise<string> {
  if (_apiEndpoint) {
    return _apiEndpoint;
  }

  try {
    const response = await fetch('./assets/apiserver.json');
    if (response.ok) {
      const config: ApiServerConfig = await response.json();
      _apiEndpoint = config.host || window.location.origin;
      _dealerConnectClientId = config.dealerConnectClientId;
      _dealerConnectDemoUserToken = config.dealerConnectDemoUserToken;
      _dealerConnectVersion = config.dealerConnectVersion;
      console.log('[DealerConnect Demo] Loaded API endpoint from apiserver.json:', _apiEndpoint);
    } else {
      _apiEndpoint = DEMO_CONFIG.defaultApiEndpoint;
      console.log('[DealerConnect Demo] apiserver.json not found, using default:', _apiEndpoint);
    }
  } catch {
    _apiEndpoint = DEMO_CONFIG.defaultApiEndpoint;
    console.log('[DealerConnect Demo] Failed to load apiserver.json, using default:', _apiEndpoint);
  }

  return _apiEndpoint;
}

/** Ensure apiserver.json has been loaded and cached. */
export async function loadDemoConfig(): Promise<void> {
  await loadApiEndpoint();
}

/**
 * Get the current API endpoint (must call loadApiEndpoint first)
 */
export function getApiEndpoint(): string {
  return _apiEndpoint ?? DEMO_CONFIG.defaultApiEndpoint;
}

export function getDealerConnectClientId(): string {
  return _dealerConnectClientId ?? '';
}

export function getDealerConnectDemoUserToken(): string {
  return _dealerConnectDemoUserToken ?? '';
}

export function getDealerConnectVersion(): string {
  return _dealerConnectVersion ?? '';
}

/**
 * Get the DealerConnect instance from globalThis
 */
export function getDealerConnect(): DealerConnectEntryPoint | undefined {
  return (globalThis as unknown as { dealerConnect?: DealerConnectEntryPoint }).dealerConnect;
}

/**
 * Demo authentication handler
 * In a real application, this would return a token from your authentication system.
 * For the demo, we return a mock token that the debug endpoint will accept.
 */
async function demoAuthenticationHandler(): Promise<string> {
  console.log('[DealerConnect Demo] Authentication handler called');

  const configuredToken = getDealerConnectDemoUserToken().trim();
  if (configuredToken) {
    return configuredToken;
  }

  // Fallback for local dev/demo: return a mock token.
  // If your license server requires a real token, set dealerConnectDemoUserToken in apiserver.json.
  return 'DEMO_EXTERNAL_TOKEN_' + Date.now();
}

async function demoAuthenticationHandlerWith2FA(): Promise<DealerConnectExternalAuth> {
  const externalToken = await demoAuthenticationHandler();

  // Always prompt for 2FA in the demo so it's never stale and you can trace
  // when/why the auth handler is being invoked.
  const twoFactorCode = await requestTwoFactorCodeFromUi();
  return {
    externalToken,
    twoFactorCode
  };
}

/**
 * Demo event handler - logs events and dispatches to the demo page
 */
async function demoEventHandler(event: DealerConnectEvent): Promise<boolean> {
  console.log('[DealerConnect Demo] Event received:', event);

  // Dispatch a custom event so the demo page can display it
  window.dispatchEvent(
    new CustomEvent('dealerconnect-event', {
      detail: event,
      bubbles: true
    })
  );

  return true;
}

/**
 * Initialize DealerConnect with demo configuration
 */
export async function initializeDealerConnect(): Promise<DealerConnectPreConfigureResult | null> {
  const dealerConnect = getDealerConnect();

  if (!dealerConnect) {
    console.error('[DealerConnect Demo] DealerConnect not found on globalThis');
    return null;
  }

  // Load API endpoint from apiserver.json or use default
  const apiEndpoint = await loadApiEndpoint();
  const dealerConnectClientId = getDealerConnectClientId().trim();
  if (!dealerConnectClientId) {
    console.error(
      '[DealerConnect Demo] dealerConnectClientId is not set. Provide it in ./assets/apiserver.json (pipeline fills this in).'
    );
    return null;
  }

  console.log('[DealerConnect Demo] DealerConnect version:', dealerConnect.version);
  console.log('[DealerConnect Demo] Starting preConfigure with endpoint:', apiEndpoint);

  try {
    const result = await dealerConnect.preConfigure({
      clientId: dealerConnectClientId,
      apiEndpoint,
      authenticationHandler: demoAuthenticationHandlerWith2FA,
      onEvent: demoEventHandler
    });

    console.log('[DealerConnect Demo] preConfigure result:', result);

    // Dispatch result to the demo page
    window.dispatchEvent(
      new CustomEvent('dealerconnect-initialized', {
        detail: result,
        bubbles: true
      })
    );

    return result;
  } catch (error) {
    console.error('[DealerConnect Demo] preConfigure failed:', error);
    return null;
  }
}
